import {
    Box,
    ContextualBanner,
    Heading,
    Info,
    LinkText,
    TextBlock,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import { t } from "i18next";
import React from "react";

import style from "./single-sign-on-details.scss";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import { SsoConfiguration, SsoProtocol } from "domain/users";

interface Props {
    ssoLoginDetails: SsoConfiguration;
}

interface Item {
    label: React.ReactNode;
    value: string;
    copyable: boolean;
}

export const SingleSignOnDetails = (props: Props) => {
    const createItems = () => {
        const items: Item[] = [
            {
                label: t("Settings.ssoConfigurations.protocol"),
                value: props.ssoLoginDetails.protocol,
                copyable: false,
            },
            {
                label: t("Settings.ssoConfigurations.singleSignOnURL"),
                value: props.ssoLoginDetails.ssoUrl,
                copyable: true,
            },
        ];

        if (props.ssoLoginDetails.protocol === SsoProtocol.SAML) {
            items.push({
                label: t("Settings.ssoConfigurations.entityID"),
                value: props.ssoLoginDetails.entityId,
                copyable: true,
            });

            items.push({
                label: t("Settings.ssoConfigurations.metadataURL"),
                value: props.ssoLoginDetails.metadataUrl,
                copyable: true,
            });
        } else {
            items.push({
                label: t("Settings.ssoConfigurations.clientID"),
                value: props.ssoLoginDetails.clientId,
                copyable: false,
            });

            items.push({
                label: t("Settings.ssoConfigurations.issuerURL"),
                value: props.ssoLoginDetails.oidcIssuer,
                copyable: true,
            });
        }

        items.push({
            label: (
                <Box gap="Xxs">
                    {t("Settings.ssoConfigurations.label")}
                    <Tooltip placement="right">
                        <TooltipContent>{t("Settings.ssoConfigurations.infoIconMessage")}</TooltipContent>
                        <TooltipTrigger>
                            <Info size={20} />
                        </TooltipTrigger>
                    </Tooltip>
                </Box>
            ),
            value: props.ssoLoginDetails.loginUrl,
            copyable: true,
        });

        return items;
    };

    const styleLabel = (label: string | React.ReactNode) => {
        const styledLabel = typeof label === "string" ? `${label.trim()}:` : label;
        return <strong>{styledLabel}</strong>;
    };

    const items = createItems();

    return (
        <Box columnLayout className={style.formContent}>
            <Heading variant="h2" underline>
                {t("Settings.ssoConfigurations.title")}
            </Heading>

            <ContextualBanner variant="Info">
                <TextBlock disableBottomSpacing>
                    {t("Settings.ssoConfigurations.message")}{" "}
                    <LinkText as="a" href={"/knowledge-base"}>
                        {t("Settings.ssoConfigurations.weblink")}
                    </LinkText>
                </TextBlock>
            </ContextualBanner>

            <Box columnLayout gap="Sm" paddingTop="Md">
                {items.map((item, index) => (
                    <Box columnLayout key={index}>
                        {styleLabel(item.label)}
                        <Box className={style.rowContent}>
                            {item.value}
                            {item.copyable && <CopyToClipboard value={item.value} isNewTable />}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
